<template>
  <div class="d-flex flex-column fill-height">
    <v-toolbar style="max-height: 45px !important" flat dense dark color="grey darken-4">
      <v-btn icon @click="selectGroup(null)" v-if="$vuetify.breakpoint.smAndDown">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <transition name="v-expand-x-transition">
        <v-text-field
          v-if="isSearchActive"
          ref="searchField"
          v-model="searchQuery"
          placeholder="Chat durchsuchen..."
          dark
          solo
          flat
          dense
          hide-details
          autofocus
          class="mx-2">
          <template v-slot:append>
            <v-btn icon small @click="clearSearch">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </transition>

      <template v-if="!isSearchActive">
        <v-toolbar-title v-if="activeGroupData" class="font-weight-bold text-truncate">
          {{ activeGroupData.name }}
        </v-toolbar-title>
      </template>

      <v-spacer></v-spacer>

      <v-btn v-if="!isSearchActive" icon @click="toggleSearch(true)">
        <v-icon>mdi-magnify</v-icon>
      </v-btn>

      <v-btn v-if="isSearchActive" icon @click="toggleSearch(false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <v-btn icon @click="showSettingsDialog = true">
        <v-icon>mdi-cog</v-icon>
      </v-btn>
    </v-toolbar>
    <ChatGroupSettingsDialog v-if="activeGroupData" v-model="showSettingsDialog" :group="activeGroupData" />
    <ReadReceiptsDialog
      v-if="selectedMessageForReceipts"
      v-model="showReadReceipts"
      :read-by="selectedMessageForReceipts.readBy"
      :members="activeGroupData.members"
      @input="
        ($event) => {
          $event ? '' : (selectedMessageForReceipts = null)
        }
      " />
    <v-progress-linear indeterminate v-if="isLoadingMessages" color="primary"></v-progress-linear>
    <v-container v-if="!isLoadingMessages && activeGroupMessages.length === 0" class="fill-height" fluid>
      <v-row align="center" justify="center" class="fill-height">
        <v-col cols="12" class="text-center grey--text text--darken-1">
          <v-icon size="80" color="grey darken-2">mdi-message-arrow-left-outline</v-icon>
          <p class="mt-4 headline">Es ist noch nichts hier</p>
          <p class="body-1">Sende die erste Nachricht!</p>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-if="searchQuery && filteredMessages.length === 0" class="fill-height" fluid>
      <v-row align="center" justify="center" class="fill-height pa-4">
        <v-col cols="12" class="text-center grey--text text--lighten-1">
          <v-icon size="60" color="grey darken-2">mdi-magnify-close</v-icon>
          <p class="mt-4 headline">Keine Treffer</p>
          <p class="body-1">Für "{{ searchQuery }}" wurden keine Nachrichten gefunden.</p>
        </v-col>
      </v-row>
    </v-container>
    <div v-else class="chat-messages-container flex-grow-1 pa-4" ref="messagesContainer">
      <div
        v-for="message in filteredMessages"
        :key="message._id"
        :id="'msg-' + message._id"
        class="d-flex mb-3"
        :class="isMyMessage(message) ? 'justify-end' : 'justify-start'">
        <v-avatar @click="viewUserProfile(message.senderId)" v-if="!isMyMessage(message)" class="mr-2 my-auto" size="36">
          <v-img v-if="message.senderId && message.senderId.bild" :src="'/img/profilbilder/' + message.senderId.bild" alt="Profilbild"></v-img>
          <v-sheet v-else color="grey darken-2" class="fill-height d-flex justify-center align-center">
            <span class="white--text text-caption">
              {{ getInitials(message.senderId) }}
            </span>
          </v-sheet>
        </v-avatar>
        <v-sheet v-if="message.isDeleted" rounded="xl" class="pa-3 message-bubble is-deleted">
          <v-icon small class="mr-1" color="red">mdi-delete-empty</v-icon>
          <em>Diese Nachricht wurde gelöscht.</em>
        </v-sheet>

        <template v-else>
          <v-menu class="glass-menu" offset-y :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-sheet
                elevation="2"
                rounded="xl"
                class="pa-3 message-bubble"
                :class="{
                  'is-me': isMyMessage(message),
                  'is-other': !isMyMessage(message),
                  'is-my-mention': isMyMention(message), // <-- NEU
                }"
                v-bind="attrs"
                v-on="on"
                style="cursor: pointer"
                :data-message-id="message._id">
                <v-sheet
                  v-if="message.replyToMessageId"
                  class="reply-bubble mb-2"
                  rounded="lg"
                  @click.stop="scrollToMessage(message.replyToMessageId._id)">
                  <template v-if="message.replyToMessageId.isDeleted">
                    <div class="pa-2 grey--text" style="font-style: italic">
                      <v-icon small color="red">mdi-delete-empty</v-icon>
                      Gelöschte Nachricht
                    </div>
                  </template>
                  <template v-else>
                    <div class="reply-bubble-sender font-weight-bold">
                      {{ message.replyToMessageId.senderId ? message.replyToMessageId.senderId.vorname : "..." }}
                    </div>
                    <div class="reply-bubble-content text-truncate">
                      {{ message.replyToMessageId.textContent }}
                    </div>
                  </template>
                </v-sheet>

                <div v-if="!isMyMessage(message)" class="sender-name font-weight-bold mb-1">
                  {{ message.senderId ? message.senderId.vorname : "..." }}
                </div>

                <div class="message-content" v-text="message.textContent"></div>

                <div class="message-time text-caption mt-1 text-right">
                  {{ formatTimestamp(message.createdAt) }}

                  <v-icon v-if="isMyMessage(message)" :color="getReadStatus(message).color" small class="ml-1" style="vertical-align: middle">
                    {{ getReadStatus(message).icon }}
                  </v-icon>
                </div>

                <div v-if="message.isEdited" class="message-time text-caption mt-1 text-right" style="opacity: 0.7; font-style: italic">
                  Bearbeitet
                </div>
              </v-sheet>
            </template>
            <v-list dense dark>
              <v-list-item @click="setReplyTo(message)">
                <v-list-item-icon><v-icon>mdi-reply</v-icon></v-list-item-icon>
                <v-list-item-title>Antworten</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="isMyMessage(message)" @click="editMessage(message)">
                <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                <v-list-item-title>Bearbeiten</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="isMyMessage(message)" @click="deleteMessage(message)">
                <v-list-item-icon><v-icon color="red">mdi-delete</v-icon></v-list-item-icon>
                <v-list-item-title class="red--text text--lighten-2">Löschen</v-list-item-title>
              </v-list-item>

              <v-divider v-if="isMyMessage(message)"></v-divider>
              <v-list-item v-if="isMyMessage(message)" @click="openReadReceipts(message)">
                <v-list-item-icon><v-icon>mdi-information-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Gelesen von...</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </div>
    </div>
    <div class="typing-indicator-container px-4 pb-1 d-flex align-center" style="min-height: 24px">
      <transition-group tag="div" class="d-flex align-center" name="avatar-fade">
        <v-avatar v-for="member in typingUsersList" :key="member.userId._id" size="32" class="typing-avatar">
          <v-img v-if="member.userId.bild" :src="'/img/profilbilder/' + member.userId.bild" :alt="member.userId.vorname"></v-img>
          <span v-else class="text-caption">{{ getInitials(member.userId) }}</span>
        </v-avatar>
      </transition-group>

      <span v-if="typingUsersList.length > 0" class="caption grey--text text--lighten-1 ml-2" style="font-style: italic">
        {{ typingUsersList.length > 1 ? "schreiben" : "schreibt" }} gerade
      </span>
    </div>

    <v-sheet v-if="isEditing" dark color="grey darken-2" class="pa-2 d-flex" style="margin: 0 8px 8px 8px; border-radius: 12px">
      <v-icon small class="mr-2" color="primary">mdi-pencil</v-icon>
      <div class="flex-grow-1">
        <div class="font-weight-bold" style="line-height: 1.2">Nachricht bearbeiten</div>
        <div class="text-caption text-truncate" style="line-height: 1.2">
          {{ editingMessage.textContent }}
        </div>
      </div>
      <v-btn icon small @click="cancelEditMode">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </v-sheet>

    <v-sheet
      v-if="replyingToMessage && !isEditing"
      dark
      color="grey darken-2"
      class="pa-2 d-flex align-center"
      style="margin: 0 8px 8px 8px; border-radius: 12px">
      <v-icon small class="mr-2">mdi-reply</v-icon>

      <div class="flex-grow-1" style="overflow: hidden">
        <div class="font-weight-bold" style="line-height: 1.2">
          {{ replyingToMessage.senderId ? replyingToMessage.senderId.vorname : "..." }}
        </div>
        <div class="text-caption text-truncate" style="line-height: 1.2">
          {{ replyingToMessage.textContent }}
        </div>
      </div>

      <v-btn icon small @click="setReplyTo(null)">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </v-sheet>
    <v-menu
      v-model="isPopupMenuOpen"
      :close-on-content-click="false"
      :close-on-click="false"
      :open-on-click="false"
      :open-on-hover="false"
      offset-y
      top
      content-class="glass-menu popup-menu"
      max-height="300px">
      <template v-slot:activator="{ on }">
        <v-sheet dark class="message-input-area pa-2" elevation="4" color="grey darken-3" v-on="on">
          <v-text-field
            ref="chatInput"
            v-model="newMessageText"
            @keydown.enter.prevent="sendMessage"
            @keyup.esc="closeMenus"
            placeholder="Nachricht..."
            filled
            rounded
            dense
            hide-details
            dark
            autocomplete="off">
            <template v-slot:prepend-inner>
              <v-icon @click="toggleEmojiMenu" style="cursor: pointer" :color="popupMode === 'emojis' ? 'primary' : 'grey lighten-1'">
                mdi-emoticon-outline
              </v-icon>
            </template>
            <template v-slot:append-outer>
              <v-btn fab small depressed color="primary" @click="sendMessage" :disabled="!newMessageText.trim()" :key="isEditing ? 'edit' : 'send'">
                <v-icon>{{ isEditing ? "mdi-check" : "mdi-send" }}</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </v-sheet>
      </template>
      <v-list dense v-if="popupMode === 'mentions'">
        <v-list-item v-for="member in mentionSuggestions" :key="member.userId._id" @click="selectMention(member)">
          <v-list-item-avatar size="32" class="mr-2">
            <v-img v-if="member.userId.bild" :src="'/img/profilbilder/' + member.userId.bild"></v-img>
            <span v-else>{{ getInitials(member.userId) }}</span>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>{{ member.userId.vorname }} {{ member.userId.nachname }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <div v-if="popupMode === 'emojis'">
        <Picker
          color="gray"
          :data="emojiIndex"
          set="apple"
          :native="true"
          title="Emojis"
          @select="addEmoji"
          :show-preview="false"
          :show-skin-tones="false"
          :theme="'dark'" />
      </div>
    </v-menu>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from "vuex" // (Den hast du schon)
import ReadReceiptsDialog from "./readReceiptsDialog.vue" // (Den hast du schon)

import { Picker, data } from "emoji-mart-vue"
import "emoji-mart-vue/css/emoji-mart.css"
const emojiIndex = data // 'data' ist jetzt das korrekte Objekt

export default {
  name: "ChatMessageView",
  components: {
    ChatGroupSettingsDialog: () => import("./chatGroupSettingsDialog.vue"),
    ReadReceiptsDialog,
    Picker,
  },
  data() {
    return {
      newMessageText: "",
      showSettingsDialog: false,
      isSearchActive: false,
      searchQuery: "",
      observer: null, // Hält die Observer-Instanz
      readQueue: new Set(), // Sammelt Nachrichten-IDs, die als 'gelesen' gesendet werden sollen
      readQueueTimer: null, // Timer, um das Senden zu bündeln
      isWindowFocused: true, // Verhindert "gelesen", wenn Fenster im Hintergrund ist
      showReadReceipts: false,
      selectedMessageForReceipts: null,
      typingTimer: null, // Hält den Timeout für "stoppt tippen"
      amITyping: false,
      iAmSending: false, // Wichtig für das Auto-Scrollen
      isInitialLoad: true,
      mentionMenu: false, // Steuert das Popup
      mentionSuggestions: [], // Hält die gefilterten User
      mentionQuery: "", // Hält den Suchbegriff (z.B. "seb")
      mentionStartIndex: -1,
      emojiIndex: emojiIndex, // Macht den Index im Template verfügbar
      popupMode: null, // 'null', 'mentions', oder 'emojis'
    }
  },

  computed: {
    ...mapState("userStore", ["user"]),
    ...mapState("chatStore", ["isLoadingMessages", "replyingToMessage", "editingMessage"]),
    ...mapGetters("chatStore", ["activeGroupMessages", "activeGroupData", "getTypingUsersForActiveGroup"]),
    isPopupMenuOpen: {
      get() {
        return this.popupMode !== null
      },
      set(value) {
        if (!value) {
          this.popupMode = null
        }
      },
    },
    isMyMention() {
      return (message) => {
        if (!message.mentions || !this.user) return false
        return message.mentions.includes(this.user._id)
      }
    },
    typingUsersList() {
      if (!this.user) return []
      console.log(this.getTypingUsersForActiveGroup)
      return this.getTypingUsersForActiveGroup.filter((member) => member.userId._id !== this.user._id)
    },
    isEditing() {
      return this.editingMessage !== null
    },
    filteredMessages() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return this.activeGroupMessages
      }

      const query = this.searchQuery.toLowerCase().trim()
      return this.activeGroupMessages.filter((message) => {
        if (message.isDeleted) return false
        return message.textContent && message.textContent.toLowerCase().includes(query)
      })
    },
  },

  methods: {
    // Aktionen aus dem Store mappen
    ...mapActions("chatStore", {
      selectGroup: "setActiveGroup",
      dispatchSendMessage: "sendMessage",
      dispatchDeleteMessage: "deleteMessage",
      setReplyTo: "setReplyTo",
      setEditingMessage: "setEditingMessage",
      cancelEditing: "cancelEditing",
      dispatchEditMessage: "dispatchEditMessage",
      sendReadReceipts: "sendReadReceipts", // <-- NEUE ACTION
      viewUserProfile: "viewUserProfile", // <-- NEU
      emitStartTyping: "emitStartTyping",
      emitStopTyping: "emitStopTyping",
    }),
    toggleEmojiMenu() {
      if (this.popupMode === "emojis") {
        this.popupMode = null // Schließen
      } else {
        this.popupMode = "emojis" // Öffnen
      }
    },

    /**
     * Wird vom Picker aufgerufen, wenn ein Emoji geklickt wird
     */
    addEmoji(emoji) {
      const emojiChar = emoji.native // z.B. '👍'
      const input = this.$refs.chatInput.$refs.input // Das echte <input>

      // "Richtig Geil"-Logik: An der Cursor-Position einfügen
      const start = input.selectionStart
      const end = input.selectionEnd

      const text = this.newMessageText

      // Füge den Emoji an der Cursor-Position ein
      this.newMessageText = text.substring(0, start) + emojiChar + text.substring(end)

      // Setze den Cursor HINTER den neuen Emoji
      this.$nextTick(() => {
        input.selectionStart = input.selectionEnd = start + emojiChar.length
        input.focus()
      })
    },
    toggleSearch(active) {
      this.isSearchActive = active
      if (active) {
        this.$nextTick(() => {
          this.$refs.searchField.focus()
        })
      } else {
        this.searchQuery = ""
      }
    },

    clearSearch() {
      this.searchQuery = ""
      this.$refs.searchField.focus()
    },
    openReadReceipts(message) {
      this.selectedMessageForReceipts = message
      this.showReadReceipts = true
    },
    isMyMessage(message) {
      if (!this.user || !message.senderId) return false
      const senderId = message.senderId._id || message.senderId
      return senderId === this.user._id
    },
    formatTimestamp(timestamp) {
      return new Date(timestamp).toLocaleTimeString("de-CH", {
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    getInitials(user) {
      if (!user) return "?"
      const v = user.vorname || ""
      const n = user.nachname || ""
      return `${v.charAt(0).toUpperCase()}${n.charAt(0).toUpperCase()}`
    },
    scrollToMessage(messageId) {
      // 1. Finde das DOM-Element anhand der ID, die wir im v-for gesetzt haben
      const element = document.getElementById("msg-" + messageId)

      if (element) {
        // 2. Scrolle das Element in die Mitte des Bildschirms
        element.scrollIntoView({ behavior: "smooth", block: "center" })

        // 3. "Richtig Geil"-Effekt: Highlighte die Nachricht
        //    (Dafür müssen die CSS-Klassen/Animationen im <style>-Block sein)
        element.classList.add("highlight-message")

        // 4. Entferne die Highlight-Klasse nach 1.5 Sekunden wieder
        setTimeout(() => {
          element.classList.remove("highlight-message")
        }, 1500)
      }
    },

    // --- Logik für Senden, Editieren, Löschen (bleibt gleich) ---
    // in views/chat/ChatMessageView.vue
    sendMessage() {
      const trimmedText = this.newMessageText.trim()
      if (!trimmedText) return // --- HIER IST DER FIX (LOGIK HOCHGEZOGEN) --- // 1. Scanne den Text IMMER nach Mentions

      const mentionIds = []
      if (this.activeGroupData && this.activeGroupData.members) {
        this.activeGroupData.members.forEach((member) => {
          const fullName = `@${member.userId.vorname} ${member.userId.nachname}`
          if (trimmedText.includes(fullName)) {
            mentionIds.push(member.userId._id)
          }
        })
      }

      // 2. Erstelle das *volle* Payload-Objekt
      const payload = {
        textContent: trimmedText,
        messageType: "text",
        mentionIds: mentionIds, // <-- Das Array mit den IDs
      } // 3. Entscheide, welche Action aufgerufen wird
      // --- ENDE FIX ---

      if (this.isEditing) {
        // ALT: this.dispatchEditMessage(trimmedText);
        // NEU: Wir übergeben das *volle* Payload!
        this.dispatchEditMessage(payload)
      } else {
        this.dispatchSendMessage(payload) // (War schon korrekt)
      } // 4. Aufräumen (bleibt gleich)
      this.newMessageText = ""
      this.closeMenus()
      clearTimeout(this.typingTimer)
      if (this.amITyping) {
        this.emitStopTyping()
        this.amITyping = false
      }
      this.iAmSending = true
    },
    deleteMessage(message) {
      if (confirm("Nachricht wirklich löschen?")) {
        this.dispatchDeleteMessage(message)
      }
    },
    selectMention(member) {
      const fullName = `@${member.userId.vorname} ${member.userId.nachname} ` // (Mit Leerzeichen!)

      const text = this.newMessageText
      const before = text.substring(0, this.mentionStartIndex)
      const after = text.substring(this.mentionStartIndex + this.mentionQuery.length + 1)

      // Ersetze "@seb" mit "@Sebastian Schwemer "
      this.newMessageText = before + fullName + after

      this.closeMenus()

      // Fokus zurück ins Input-Feld
      this.$nextTick(() => {
        this.$refs.chatInput.focus()
      })
    },
    closeMenus() {
      this.popupMode = null

      this.mentionSuggestions = []
      this.mentionQuery = ""
      this.mentionStartIndex = -1
    },
    editMessage(message) {
      this.setEditingMessage(message)
      this.newMessageText = message.textContent
    },
    cancelEditMode() {
      this.cancelEditing()
      this.newMessageText = ""
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },

    // --- NEU: LOGIK FÜR "GELESEN"-HÄKCHEN ---
    /**
     * Berechnet den Status (Icon & Farbe) für eine gesendete Nachricht.
     */
    getReadStatus(message) {
      // 1. Standard: Gesendet (ein graues Häkchen)
      // (Wir überspringen "Zugestellt", da wir das nicht tracken)
      let status = { icon: "mdi-check", color: "grey" }

      if (!this.activeGroupData || !this.activeGroupData.members) {
        return status
      }

      const readByList = message.readBy || []

      // 2. Gelesen von mindestens einem (doppeltes graues Häkchen)
      if (readByList.length > 0) {
        status = { icon: "mdi-check-all", color: "grey" }
      }

      // 3. Gelesen von ALLEN (doppeltes 'primary'-farbenes Häkchen)
      // Wir zählen, wie viele Leute (außer mir) in der Gruppe sind
      const totalOtherMembers = this.activeGroupData.members.filter((m) => m.userId._id !== this.user._id).length

      // Wenn die Anzahl der Leser gleich groß oder größer ist...
      if (readByList.length >= totalOtherMembers) {
        status = { icon: "mdi-check-all", color: "primary" }
      }

      return status
    },

    // --- NEU: LOGIK FÜR INTERSECTION OBSERVER ---
    /**
     * Startet den Observer, der das Scroll-Container-Element überwacht.
     */
    initReadObserver() {
      const options = {
        root: this.$refs.messagesContainer, // Der Scroll-Container
        threshold: 0.8, // 80% der Nachricht muss sichtbar sein
      }

      this.observer = new IntersectionObserver(this.handleObserverCallback, options)

      // Starte die Beobachtung für alle *aktuell* sichtbaren Nachrichten
      this.observeMessages()
    },
    handleTextInput(value) {
      // 1. Wenn ich noch nicht als "tippend" markiert bin -> Sende Start-Event
      if (!this.amITyping && value) {
        this.amITyping = true
        this.emitStartTyping()
      }

      // 2. Alte "Stop"-Timer löschen
      clearTimeout(this.typingTimer)

      // 3. Neuen "Stop"-Timer starten (nach 2 Sekunden Inaktivität)
      this.typingTimer = setTimeout(() => {
        if (this.amITyping) {
          this.emitStopTyping()
          this.amITyping = false
        }
      }, 2000) // 2 Sekunden
    },
    /**
     * Weist den Observer an, alle noch nicht beobachteten Nachrichten
     * (als "gelesen" markierbar) zu beobachten.
     */
    observeMessages() {
      if (!this.observer) return

      // Finde alle Bubbles, die WIR NICHT GESENDET HABEN
      // und die wir noch NICHT BEOBACHTET haben.
      const unreadBubbles = this.$refs.messagesContainer.querySelectorAll(".message-bubble.is-other:not(.observed)")

      unreadBubbles.forEach((bubble) => {
        const messageId = bubble.dataset.messageId
        const message = this.activeGroupMessages.find((m) => m._id === messageId)

        // Füge sie nur hinzu, wenn wir sie WIRKLICH noch nicht gelesen haben
        if (message && !this.isMessageReadByMe(message)) {
          bubble.classList.add("observed")
          this.observer.observe(bubble)
        }
      })
    },

    /**
     * Wird vom Observer aufgerufen, wenn ein Element in den Viewport kommt.
     */
    handleObserverCallback(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting && this.isWindowFocused) {
          const messageId = entry.target.dataset.messageId
          if (messageId) {
            this.readQueue.add(messageId) // Zur Warteschlange hinzufügen
            this.scheduleReadQueueSend() // Sende-Timer starten
            this.observer.unobserve(entry.target) // Nur einmal auslösen
          }
        }
      })
    },

    /**
     * Bündelt die "gelesen"-Events
     */
    scheduleReadQueueSend() {
      if (this.readQueueTimer) return // Timer läuft schon

      this.readQueueTimer = setTimeout(() => {
        if (this.readQueue.size > 0 && this.activeGroupData) {
          const messageIds = Array.from(this.readQueue)
          // Ruft die Vuex-Action auf, die das Socket-Event sendet
          this.sendReadReceipts({
            groupId: this.activeGroupData._id,
            messageIds: messageIds,
          })
          this.readQueue.clear()
        }
        this.readQueueTimer = null
      }, 2000) // Sendet alle 2 Sekunden
    },

    /**
     * Prüft, ob der EINGELOGGTE User eine Nachricht schon gelesen hat
     */
    isMessageReadByMe(message) {
      if (!message || !this.user) return true // (Sicherheit)
      // Eigene Nachrichten gelten als "gelesen"
      if (this.isMyMessage(message)) return true

      if (!message.readBy) return false
      return message.readBy.some((r) => r.userId === this.user._id)
    },

    // --- NEU: Fenster-Fokus-Management ---
    handleFocus() {
      this.isWindowFocused = true
    },
    handleBlur() {
      this.isWindowFocused = false
    },

    /**
     * Räumt alle Timer und Observer auf
     */
    cleanupObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
      clearTimeout(this.readQueueTimer)
      this.readQueueTimer = null
      this.readQueue.clear()

      window.removeEventListener("focus", this.handleFocus)
      window.removeEventListener("blur", this.handleBlur)
    },
  },

  watch: {
    newMessageText(newValue) {
      // 1. Tipp-Indikator (bleibt gleich)
      if (this.isEditing) {
        /* (nichts tun) */
      } else {
        this.handleTextInput(newValue)
      }

      // 2. @Mention-Logik
      try {
        const input = this.$refs.chatInput.$refs.input // Hol das echte <input>-Element
        const cursorPosition = input.selectionStart // Wo ist der Cursor?

        // Finde das letzte '@' VOR dem Cursor
        const textBeforeCursor = newValue.substring(0, cursorPosition)
        const atIndex = textBeforeCursor.lastIndexOf("@")

        // Finde das letzte Leerzeichen nach dem '@'
        const spaceAfterAt = textBeforeCursor.indexOf(" ", atIndex)

        // BEDINGUNG:
        // 1. Es gibt ein '@' (atIndex > -1)
        // 2. Es gibt *kein* Leerzeichen zwischen '@' und Cursor (spaceAfterAt === -1)
        if (atIndex > -1 && spaceAfterAt === -1) {
          // Wir sind im "@mention"-Modus!
          this.mentionQuery = textBeforeCursor.substring(atIndex + 1).toLowerCase()
          this.mentionStartIndex = atIndex // Merken, wo wir ersetzen müssen

          // Filtere die Mitgliederliste
          this.mentionSuggestions = this.activeGroupData.members.filter((member) => {
            if (member.userId._id === this.user._id) return false // Nicht sich selbst
            const name = `${member.userId.vorname} ${member.userId.nachname}`.toLowerCase()
            return name.includes(this.mentionQuery)
          })

          if (this.mentionSuggestions.length > 0) {
            this.popupMode = "mentions" // Öffne im "Mention"-Modus
          } else {
            this.popupMode = null
          }
        } else {
          this.popupMode = null // Menü schließen
        }
      } catch (e) {
        this.popupMode = null
      }
    },
    filteredMessages: {
      handler(newMessages, oldMessages) {
        // Sicherstellen, dass alles da ist
        if (!newMessages || !this.$refs.messagesContainer) {
          return
        }

        console.log("Watcher triggered for filteredMessages")

        // --- FIX 1: Der 'ReferenceError' ---
        const newLength = newMessages.length
        // 'oldMessages' kann 'undefined' sein -> Fallback auf 0
        const oldLength = oldMessages ? oldMessages.length : 0 // <-- HIER IST DER FIX

        const container = this.$refs.messagesContainer

        // Hat sich die Liste geändert?
        const wasMessageAdded = newLength > oldLength

        this.$nextTick(() => {
          console.log(newLength, "neui length")
          // --- FIX 2: Das "Scrollen beim Öffnen" ---
          // FALL 1: Erster Ladevorgang (Dein Wunsch "beim chat öffnen")
          if (this.isInitialLoad && newLength > 0) {
            this.scrollToBottom()
            this.isInitialLoad = false // Flag zurücksetzen
          } else if (wasMessageAdded) {
            console.log("hoiiii")
            this.scrollToBottom() // Nur scrollen, wenn du schon unten warst
          }

          // "Gelesen"-Observer immer aktualisieren
          this.observeMessages()
        })
      },
      deep: true,
    },
    activeGroupMessages: {
      handler(newMessages, oldMessages) {
        const newLength = newMessages ? newMessages.length : 0
        const oldLength = oldMessages ? oldMessages.length : 0

        // Nur scrollen, wenn NACHRICHTEN HINZUGEFÜGT wurden
        if (newLength > oldLength) {
          // Prüfen, ob wir schon unten waren, bevor neue Nachrichten kamen
          const container = this.$refs.messagesContainer
          const shouldScroll = container.scrollTop + container.clientHeight >= container.scrollHeight - 100 // Toleranz

          if (shouldScroll) {
            this.scrollToBottom()
          }
        }

        // NEU: Beobachte die neuen Nachrichten-Bubbles
        this.$nextTick(() => {
          this.observeMessages()
        })
      },
      deep: true,
    },
    activeGroupData(newData, oldData) {
      // Wenn sich die Gruppe ÄNDERT:
      if (newData && (!oldData || newData._id !== oldData._id)) {
        this.cleanupObserver() // (Das ist für die "Gelesen"-Logik)
        this.isInitialLoad = true // Sag dem Nachrichten-Watcher: "Gleich scrollen!"
        this.$nextTick(() => {
          this.initReadObserver()
        })
      }
    },

    editingMessage(newMessage) {
      if (newMessage) {
        this.newMessageText = newMessage.textContent
      } else {
        if (!this.replyingToMessage) {
          this.newMessageText = ""
        }
      }
    },
  },

  mounted() {
    // Wenn die Komponente geladen wird, starte den Observer
    if (this.activeGroupData) {
      this.initReadObserver()
      this.scrollToBottom()
    }
    // Prüfen, ob das Fenster im Vordergrund ist
    window.addEventListener("focus", this.handleFocus)
    window.addEventListener("blur", this.handleBlur)
  },

  beforeDestroy() {
    // WICHTIG: Alles aufräumen, um Memory-Leaks zu verhindern
    this.cleanupObserver()
    clearTimeout(this.typingTimer)
  },
}
</script>

<style lang="scss" scoped>
::v-deep .emoji-mart {
  background-color: #1e1e1e !important;
}
/* STYLING FÜR DIE NACHRICHTEN
*/
.chat-messages-container {
  overflow-y: auto;
  height: 0; /* Der Flexbox-Trick für 'overflow-y' */
}

/* Input-Bereich unten festtackern */
.message-input-area {
  flex-grow: 0;
  flex-shrink: 0;
}
.reply-bubble {
  /* Passt sich automatisch an (dunkel bei 'is-other', hell bei 'is-me') */
  background-color: rgba(255, 255, 255, 0.1);
  border-left: 3px solid var(--v-primary); /* Dein grüner Akzent */
  cursor: pointer;
  overflow: hidden; /* Stellt sicher, dass 'text-truncate' funktioniert */

  /* Dunklerer Text für 'is-me' (helle) Bubbles */
  .is-me & {
    background-color: rgba(0, 0, 0, 0.1);
    border-left-color: white;
  }
}

.reply-bubble-sender {
  padding: 4px 8px 0 8px;
  font-size: 0.8rem;
  color: var(--v-primary);

  .is-me & {
    color: white;
  }
}

.reply-bubble-content {
  padding: 0 8px 4px 8px;
  font-size: 0.9rem;
  opacity: 0.8;
  color: white;
}
/*
  "Richtig Geil" Upgrade:
  1. Glassmorphism für Menüs & Antworten
  2. Pop-In Animationen
  3. Glow-Effekt für deine Bubbles
*/

/* === GLASSMORPHISM === */
.glass-menu {
  /* Helles Glas-Design */
  background: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 12px !important;
}

.reply-bubble {
  /* Dunkles Glas-Design (passt besser in die Bubble) */
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);
  border-left: 3px solid var(--v-primary-base);
  cursor: pointer;
  overflow: hidden;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }

  /* Helles Glas für 'is-me' (helle) Bubbles */
  .is-me & {
    background-color: rgba(255, 255, 255, 0.2);
    border-left-color: white;
    &:hover {
      background-color: rgba(255, 255, 255, 0.3);
    }
  }
}

.reply-bubble-content {
  padding: 0 8px 4px 8px;
  font-size: 0.9rem;
  opacity: 0.8;
}

/* === GLOW & BUBBLE STYLING === */
.message-bubble {
  max-width: 75%;
  min-width: 80px;
  word-break: break-word;
  /* NEU: Animations-Aufruf */
  box-shadow: 0 0 6px 0px rgba(255, 255, 255, 0.3) !important;

  animation: bubble-pop-in 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  &.is-my-mention {
    background-color: #3a3000; /* Dunkelgelber Hintergrund */
    border: 1px solid #ffd700; /* Goldener Rand */

    box-shadow: 0 0 15px 0px rgba(255, 215, 0, 0.5) !important;
  }
  &.is-me {
    background: linear-gradient(to right, var(--v-primary), #01883a);
    color: white;
    /* NEU: "Glow"-Effekt */
    box-shadow: 0 0 20px -3px rgba(0, 149, 64, 1) !important;

    .message-time {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  &.is-other {
    background-color: #272727;
    color: #ffffff;
    .sender-name {
      color: var(--v-primary);
    }
    .message-time {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  /* NEU: Gelöschte Bubble */
  &.is-deleted {
    background-color: transparent !important;
    border: 1px dashed #444;
    color: #888;
    font-size: 0.9rem;
    box-shadow: none;
    font-style: italic;
  }
}

/* === ANIMATIONEN === */
@keyframes bubble-pop-in {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Scroll-Highlight-Animation (bleibt gleich) */
::v-deep .highlight-message > * {
  animation: highlight 1.5s ease-out;
}
@keyframes highlight {
  0% {
    background-color: rgba(0, 149, 64, 0.5);
  }
  100% {
    background-color: rgba(19, 225, 108, 0.5);
  }
}
/* Basis-Layout (bleibt gleich) */
.chat-messages-container {
  overflow-y: auto;
  height: 0;
}
.message-input-area {
  flex-grow: 0;
  flex-shrink: 0;
}

.typing-avatar {
  border: 2px solid #272727;
  transition: all 0.3s ease;
}
.typing-avatar + .typing-avatar {
  margin-left: -12px; /* Der Überlappungs-Effekt */
}

/* Die 3 animierten Punkte */
.typing-dots-container {
  display: flex;
  align-items: center;
}
.dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: #616161; /* grey darken-1 */
  margin: 0 1px;
  animation: typing-bounce 1.2s infinite ease-in-out;
}
.dot:nth-child(2) {
  animation-delay: -0.16s;
}
.dot:nth-child(3) {
  animation-delay: -0.32s;
}

@keyframes typing-bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}
.avatar-fade-enter-active,
.avatar-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.avatar-fade-enter,
.avatar-fade-leave-to {
  opacity: 0;
  transform: scale(0.8) translateX(-10px);
}
/* Stellt sicher, dass sich die Liste smooth bewegt,
   wenn ein Element verschwindet */
.avatar-fade-move {
  transition: transform 0.3s ease;
}
.mention-menu {
  /* (Nutzt das 'glass-menu'-Styling, das wir schon haben) */
  margin-bottom: 8px; /* Hebt es vom Input-Feld ab */
  max-height: 250px;
  overflow-y: auto;
}

::v-deep .emoji-mart-anchor {
  color: white !important;
}

::v-deep .emoji-mart-category-label {
  color: white !important;
  background-color: #1e1e1e !important;
}
</style>
